// "Not authorised" screen (ADR-0004 / issue #4; route 403 — issue #209).
//
// Two reuse sites:
//   - `account` (default) — the Entra callback authenticated a valid Microsoft
//     identity the global `users` allowlist does not provision for Septima Wealth
//     (unknown email / Inactive). Reached via the callback's redirect to
//     /not-authorised?email=…; no session cookie was minted, so sign-out simply
//     clears the Microsoft session so the user can try another account.
//   - `route` — a signed-in staff user reached a page their active persona is
//     not authorised for (a saved / typed URL). `FrontendLayout` renders this
//     IN PLACE (issue #209) — the URL is kept, no redirect — so the user sees
//     WHY rather than a blank/broken page. The sidebar leads back to a page they
//     can access, so no sign-out button is shown.

import { ShieldAlert } from 'lucide-react';
import { useSearchParams } from 'react-router';

import Image from '@/lib/Image';
import { getSeptimaLogoPath } from '@/lib/logos';
import { signOut } from '@/lib/auth';
import { Button } from '@/components/ui';

interface NotAuthorisedPageProps {
    /**
     * `account` (default) — the signed-in identity is not provisioned at all.
     * `route` — provisioned, but the active persona may not access this page.
     */
    variant?: 'account' | 'route';
}

export default function NotAuthorisedPage({ variant = 'account' }: NotAuthorisedPageProps) {
    const [params] = useSearchParams();
    const email = params.get('email');
    const isRoute = variant === 'route';

    async function handleSignOut() {
        await signOut({ callbackUrl: '/login' });
    }

    return (
        <main className="min-h-full flex flex-col bg-[rgb(var(--color-page))] text-[rgb(var(--color-text-primary))]">
            <div className="mx-auto flex min-h-full w-full max-w-[1280px] flex-col px-6 py-8 md:px-12 md:py-10">
                {/* Top brand row */}
                <header className="flex items-center gap-3">
                    <Image
                        src={getSeptimaLogoPath()}
                        alt="Septima"
                        width={22}
                        height={22}
                        className="dark:invert"
                        priority
                    />
                    <span className="text-body-medium text-[rgb(var(--color-text-secondary))]">
                        Septima Wealth
                    </span>
                </header>

                <div className="flex flex-1 items-center justify-center py-10">
                    <div className="w-full max-w-[460px] text-center">
                        <span className="mx-auto mb-6 flex h-12 w-12 items-center justify-center rounded-full border border-[rgb(var(--color-rag-amber)/0.35)] bg-[rgb(var(--color-rag-amber)/0.08)]">
                            <ShieldAlert className="h-5 w-5 text-[rgb(var(--color-rag-amber))]" />
                        </span>

                        <h1 className="text-[32px] leading-[36px] font-medium tracking-[-0.025em] text-[rgb(var(--color-text-primary))]">
                            {isRoute
                                ? 'You do not have access to this page'
                                : 'This account is not provisioned for Septima Wealth'}
                        </h1>

                        <p className="mt-4 text-body text-[rgb(var(--color-text-secondary))]">
                            {isRoute
                                ? 'Your current view does not include this page. Use the sidebar to return to a page you have access to, or contact your Septima Wealth administrator if you believe this is a mistake.'
                                : 'You signed in with a valid Microsoft account, but it has not been granted access to the Septima platform. Contact your Septima Wealth administrator to be added to the user directory.'}
                        </p>

                        {!isRoute && email && (
                            <p className="mt-5 text-annotation text-[rgb(var(--color-text-tertiary))]">
                                Signed in as{' '}
                                <span className="font-mono text-[rgb(var(--color-text-secondary))]">
                                    {email}
                                </span>
                            </p>
                        )}

                        {!isRoute && (
                            <Button
                                variant="secondary"
                                onClick={handleSignOut}
                                className="mt-8 h-[44px] px-6"
                            >
                                Sign out and use a different account
                            </Button>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <footer className="flex items-center justify-between text-caption text-[rgb(var(--color-text-tertiary))]">
                    <span>Septima Wealth</span>
                    <span className="hidden md:inline">v0.1 · 2026</span>
                </footer>
            </div>
        </main>
    );
}
